import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor/BlockEditorContext";
import { noop } from "lodash";
import type { MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";

import { AI_BLOCK_CONSTANTS } from "../constants";
import { useAiBlockContext } from "../hooks/useAiBlockContext";
import { useCompletionsStream } from "../hooks/useCompletionStream";
import { useFormSubmitDisabler } from "../hooks/useFormSubmitDisabler";

interface SubmittableEvent {
  preventDefault: () => void;
  stopPropagation: () => void;
}

interface DraftPromptNode {
  attrs?: { id?: string; placeholder?: string };
}

export interface UseDraftPromptResult {
  addPromptPrefix: (value: string, promptPrefix: string) => void;
  cleanupNode: () => void;
  handleSubmit: (event: SubmittableEvent) => void;
  inputRef: MutableRefObject<HTMLTextAreaElement | null>;
  isLoading: boolean;
  node: DraftPromptNode;
  setUserPrompt: (value: string) => void;
  userPrompt: string;
}

export const useDraftPrompt = (): UseDraftPromptResult => {
  const [userPrompt, setUserPrompt] = useState("");
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { createCompletion, isLoading } = useCompletionsStream();
  const aiCopilotTitle = useBlockEditorContext()?.aiCopilotTitle;
  const { editor, getPos, setIsLoadingResponse, cleanupNode, node } =
    useAiBlockContext();
  const [promptType, setPromptType] = useState(
    AI_BLOCK_CONSTANTS.AI_BLOCK_TYPES.HELP_WRITE,
  );

  useFormSubmitDisabler({ isLoading });

  useEffect(() => {
    // Node views can mount before their DOM is attached, and editor focus is deferred.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    setIsLoadingResponse(isLoading);
  }, [isLoading, setIsLoadingResponse]);

  const scrollContainer = () => {
    const containers = document.querySelectorAll("[data-ai-scrollable]");
    containers.forEach(container => {
      container.scrollTop =
        container.scrollTop + AI_BLOCK_CONSTANTS.GENERATED_BLOCK_HEIGHT;
    });
  };

  const insertCompletion = (data: string) => {
    editor.commands.insertContent(data);
    scrollContainer();
  };

  const insertEmptyBlock = () => {
    const position = getPos();
    if (position === undefined) return;
    editor.commands.insertContentAt(position, "\n");
    setUserPrompt("");
  };

  const onStreamClose = () => {
    setPromptType(AI_BLOCK_CONSTANTS.AI_BLOCK_TYPES.HELP_WRITE);
    setUserPrompt("");
    setIsInputDisabled(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (event: SubmittableEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (!userPrompt || isInputDisabled) {
      return;
    }

    setIsInputDisabled(true);

    const position = getPos();
    if (position === undefined) {
      setIsInputDisabled(false);
      return;
    }

    createCompletion({
      type: promptType,
      title: aiCopilotTitle,
      user_prompt: userPrompt,
      previous_text: editor.state.doc.textBetween(0, position, "\n", ". "),
      onMessage: insertCompletion,
      onOpen: insertEmptyBlock,
      onClose: onStreamClose,
      onError: () => {
        setIsInputDisabled(false);
        inputRef.current?.focus();
      },
    }).catch(noop);
  };

  const addPromptPrefix = (value: string, promptPrefix: string) => {
    setPromptType(value);
    setUserPrompt(promptPrefix);
    inputRef.current?.focus();
  };

  return {
    addPromptPrefix,
    cleanupNode,
    handleSubmit,
    inputRef,
    isLoading,
    node,
    setUserPrompt,
    userPrompt,
  };
};
